import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

function pad(value) {
  return String(value).padStart(2, '0')
}

function parseTimeText(text) {
  if (!text) return null
  const clean = text.trim().toLowerCase()
  // Try 12-hour format: hh:mm am/pm or hh am/pm
  const match12 = clean.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/)
  if (match12) {
    let hour = parseInt(match12[1], 10)
    const minute = match12[2] ? parseInt(match12[2], 10) : 0
    const period = match12[3]
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null
    if (period === 'pm' && hour < 12) hour += 12
    if (period === 'am' && hour === 12) hour = 0
    return `${pad(hour)}:${pad(minute)}`
  }
  return null
}

function formatDisplayTime(value) {
  if (!value) return ''
  const parts = value.split(':')
  let hour = parseInt(parts[0], 10)
  const minute = parseInt(parts[1], 10)
  if (isNaN(hour) || isNaN(minute)) return ''
  const period = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour % 12 || 12
  return `${pad(displayHour)}:${pad(minute)} ${period}`
}

export default function AppTimeInput({
  value,
  onChange,
  disabled = false,
  className = '',
  inputClassName = '',
  name,
  id,
  title,
  iconClassName = '',
  buttonClassName = '',
  placeholder = 'hh:mm AM/PM',
}) {
  const wrapperRef = useRef(null)
  const panelRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [panelStyle, setPanelStyle] = useState({ top: 0, left: 0, width: 240 })
  const [typedText, setTypedText] = useState(null) // null = not typing

  const activeHourRef = useRef(null)
  const activeMinuteRef = useRef(null)

  // Parse current value
  const { currentHour, currentMinute, currentPeriod } = useMemo(() => {
    if (!value) return { currentHour: 10, currentMinute: 0, currentPeriod: 'AM' }
    const parts = value.split(':')
    let h = parseInt(parts[0], 10)
    const m = parseInt(parts[1], 10)
    if (isNaN(h) || isNaN(m)) return { currentHour: 10, currentMinute: 0, currentPeriod: 'AM' }
    const p = h >= 12 ? 'PM' : 'AM'
    const displayH = h % 12 || 12
    return { currentHour: displayH, currentMinute: m, currentPeriod: p }
  }, [value])

  const hoursList = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), [])
  const minutesList = useMemo(() => Array.from({ length: 60 }, (_, i) => i), [])

  useEffect(() => {
    if (!isOpen) return undefined
    const onPointerDown = (event) => {
      const clickedInsideTrigger = wrapperRef.current?.contains(event.target)
      const clickedInsidePanel = panelRef.current?.contains(event.target)
      if (!clickedInsideTrigger && !clickedInsidePanel) {
        setIsOpen(false)
      }
    }
    window.addEventListener('pointerdown', onPointerDown)
    return () => window.removeEventListener('pointerdown', onPointerDown)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return undefined

    const computePanelPosition = () => {
      const triggerRect = wrapperRef.current?.getBoundingClientRect()
      if (!triggerRect) return

      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
      const desiredWidth = 240
      const panelHeight = 250
      const gap = 8

      let left = triggerRect.left
      let top = triggerRect.bottom + gap

      if (left + desiredWidth > viewportWidth - 8) {
        left = viewportWidth - desiredWidth - 8
      }
      left = Math.max(8, left)

      if (top + panelHeight > viewportHeight - 8) {
        top = triggerRect.top - panelHeight - gap
      }
      top = Math.max(8, top)

      setPanelStyle({
        top: Math.round(top),
        left: Math.round(left),
        width: desiredWidth,
      })
    }

    computePanelPosition()
    window.addEventListener('resize', computePanelPosition)
    window.addEventListener('scroll', computePanelPosition, true)

    // Auto-scroll selected values to center
    const timer = setTimeout(() => {
      activeHourRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' })
      activeMinuteRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' })
    }, 50)

    return () => {
      window.removeEventListener('resize', computePanelPosition)
      window.removeEventListener('scroll', computePanelPosition, true)
      clearTimeout(timer)
    }
  }, [isOpen, currentHour, currentMinute])

  const openPicker = () => {
    if (disabled) return
    setIsOpen(true)
  }

  const handleHourSelect = (h) => {
    let rawHour = h
    if (currentPeriod === 'PM' && rawHour < 12) rawHour += 12
    if (currentPeriod === 'AM' && rawHour === 12) rawHour = 0
    onChange?.(`${pad(rawHour)}:${pad(currentMinute)}`)
  }

  const handleMinuteSelect = (m) => {
    let rawHour = currentHour
    if (currentPeriod === 'PM' && rawHour < 12) rawHour += 12
    if (currentPeriod === 'AM' && rawHour === 12) rawHour = 0
    onChange?.(`${pad(rawHour)}:${pad(m)}`)
  }

  const handlePeriodSelect = (p) => {
    let rawHour = currentHour
    if (p === 'PM' && rawHour < 12) rawHour += 12
    if (p === 'AM' && rawHour === 12) rawHour = 0
    onChange?.(`${pad(rawHour)}:${pad(currentMinute)}`)
  }

  const commitTypedText = () => {
    if (typedText === null) return
    const trimmed = typedText.trim()
    if (!trimmed) {
      onChange?.('')
      setTypedText(null)
      return
    }
    const parsed = parseTimeText(trimmed)
    if (parsed) {
      onChange?.(parsed)
    }
    setTypedText(null)
  }

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <input
        id={id}
        name={name}
        type="text"
        value={typedText !== null ? typedText : formatDisplayTime(value)}
        disabled={disabled}
        title={title}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          if (disabled) return
          setTypedText(e.target.value)
          setIsOpen(false)
        }}
        onFocus={() => {
          if (disabled) return
          if (typedText === null) setTypedText(formatDisplayTime(value))
        }}
        onBlur={commitTypedText}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commitTypedText()
          } else if (e.key === 'Escape') {
            setTypedText(null)
          }
        }}
        className={`w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2.5 pr-10 text-sm font-medium text-on-surface shadow-sm outline-none transition hover:border-primary/35 focus:ring-2 focus:ring-primary/25 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer ${inputClassName}`}
        onClick={openPicker}
      />
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-on-surface-variant transition hover:bg-surface-container-low hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 ${buttonClassName}`}
        aria-label="Open time picker"
      >
        <span className={`material-symbols-outlined text-base ${iconClassName}`}>schedule</span>
      </button>

      {isOpen && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={panelRef}
            style={{ top: panelStyle.top, left: panelStyle.left, width: panelStyle.width }}
            className="fixed z-[560] overflow-hidden rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-4 pt-5 pb-3 shadow-[0_14px_34px_rgba(15,23,42,0.22)] backdrop-blur-xl"
          >
            <div className="flex gap-3 h-36 justify-center items-stretch">
              {/* Hour column */}
              <div className="flex flex-col items-center flex-1 min-w-0">
                <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider mb-2">Hour</span>
                <div className="overflow-y-auto h-28 w-full flex flex-col gap-1 pr-1 scroll-smooth app-hide-scrollbar border border-outline-variant/10 rounded-lg bg-surface-container-low/30 p-1">
                  {hoursList.map((h) => (
                    <button
                      key={h}
                      ref={h === currentHour ? activeHourRef : null}
                      type="button"
                      onClick={() => handleHourSelect(h)}
                      className={clsx(
                        "h-8 w-full shrink-0 rounded-md text-xs font-bold transition flex items-center justify-center border-0 cursor-pointer",
                        h === currentHour
                          ? "bg-primary text-on-primary"
                          : "text-on-surface hover:bg-surface-container-high"
                      )}
                    >
                      {pad(h)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Minute column */}
              <div className="flex flex-col items-center flex-1 min-w-0">
                <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider mb-2">Min</span>
                <div className="overflow-y-auto h-28 w-full flex flex-col gap-1 pr-1 scroll-smooth app-hide-scrollbar border border-outline-variant/10 rounded-lg bg-surface-container-low/30 p-1">
                  {minutesList.map((m) => (
                    <button
                      key={m}
                      ref={m === currentMinute ? activeMinuteRef : null}
                      type="button"
                      onClick={() => handleMinuteSelect(m)}
                      className={clsx(
                        "h-8 w-full shrink-0 rounded-md text-xs font-bold transition flex items-center justify-center border-0 cursor-pointer",
                        m === currentMinute
                          ? "bg-primary text-on-primary"
                          : "text-on-surface hover:bg-surface-container-high"
                      )}
                    >
                      {pad(m)}
                    </button>
                  ))}
                </div>
              </div>

              {/* AM/PM column */}
              <div className="flex flex-col items-center flex-initial">
                <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider mb-2">Period</span>
                <div className="flex flex-col gap-1 border border-outline-variant/10 rounded-lg bg-surface-container-low/30 p-1">
                  {['AM', 'PM'].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handlePeriodSelect(p)}
                      className={clsx(
                        "h-8 w-12 rounded-md text-xs font-bold transition flex items-center justify-center border-0 cursor-pointer",
                        p === currentPeriod
                          ? "bg-primary text-on-primary"
                          : "text-on-surface hover:bg-surface-container-high"
                      )}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end border-t border-outline-variant/20 pt-2.5 mt-2 shrink-0">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-md px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 border-0 cursor-pointer bg-transparent"
              >
                Done
              </button>
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  )
}
